import type { EquipmentConfig, ExtraTagConfig, StreamConfig } from '../../engine/plant/types';

/**
 * Atmospheric crude distillation unit, 75 000 bpd (500 m³/h). Design conditions and the
 * reduced-order column model: docs/PROCESS_MODELS.md §2.9 and §5.3.
 *
 * Species are pseudo-components: light ends, naphtha, kerosene, diesel and residue.
 */

export const extraTags: ExtraTagConfig[] = [
  { name: 'S-101.Q', units: 'm3/h', rangeLo: 0, rangeHi: 800, decimals: 1, kind: 'state', description: 'Crude charge flow' },
  { name: 'S-CW.Q', units: 'm3/h', rangeLo: 0, rangeHi: 900, decimals: 1, kind: 'state', description: 'Condenser cooling-water flow' },
  { name: 'S-303.Q', units: 'm3/h', rangeLo: 0, rangeHi: 500, decimals: 1, kind: 'state', description: 'Reflux flow' },
  { name: 'S-304.Q', units: 'm3/h', rangeLo: 0, rangeHi: 200, decimals: 1, kind: 'state', description: 'Naphtha product flow' },
];

const productValve = (id: string, cv: number, initial: number, sg = 0.82): EquipmentConfig => ({
  id,
  name: `${id} control valve`,
  type: 'valve',
  cv,
  characteristic: 'equalPercentage',
  tau: 3,
  deadband: 0.5,
  failMode: 'failClosed',
  commandTag: `${id}.CMD`,
  initialPosition: initial,
  positionTransmitter: true,
  specificGravity: sg,
});

export const equipment: EquipmentConfig[] = [
  // ── Crude charge and preheat ────────────────────────────────────────────
  {
    id: 'SRC-CRUDE',
    name: 'Crude from tankage',
    type: 'source',
    // Tank head only: the charge pump provides all the pressure, so tripping it really
    // does stop the flow through the coil.
    pressure: 1.5e5,
    temperature: 300,
    // A light Arabian-style assay: the numbers are volume fractions × 10.
    composition: { light: 0.4, nap: 1.2, ker: 1.8, dsl: 2.5, res: 4.1 },
    profiles: [
      { field: 'res', kind: 'constant', mean: 4.1, noiseSigma: 0.004, noisePhi: 0.999, lo: 2, hi: 7 },
    ],
  },
  {
    id: 'P-101',
    name: 'Crude charge pump',
    type: 'pump',
    h0: 220,
    // H = H0 − k·Q²: 20 % droop from shut-off to the best-efficiency flow.
    kCurve: 2245,
    qBep: 0.14,
    etaBep: 0.79,
    vfd: false,
    commandTag: 'P-101.CMD',
    startTime: 12,
    coastTime: 25,
    npshr: 4,
  },
  productValve('FV-101', 900, 0.66),
  {
    id: 'HX-101',
    name: 'Preheat vs kerosene',
    type: 'hx',
    ua: 2.6e5,
    volHot: 6,
    volCold: 4,
    rhoCpHot: 1.9e6,
    rhoCpCold: 1.9e6,
    coldSupplyTag: 'C-301.T_KER',
    coldFlowTag: 'CALC-305.OUT',
    initialHot: 331,
    initialCold: 400,
  },
  {
    id: 'HX-102',
    name: 'Preheat vs diesel',
    type: 'hx',
    ua: 3.4e5,
    volHot: 8,
    volCold: 6,
    rhoCpHot: 1.9e6,
    rhoCpCold: 1.9e6,
    coldSupplyTag: 'C-301.T_DSL',
    coldFlowTag: 'CALC-306.OUT',
    initialHot: 394,
    initialCold: 470,
  },
  {
    id: 'HX-103',
    name: 'Preheat vs residue',
    type: 'hx',
    ua: 4.6e5,
    volHot: 10,
    volCold: 8,
    rhoCpHot: 1.9e6,
    rhoCpCold: 1.9e6,
    coldSupplyTag: 'C-301.T_RES',
    coldFlowTag: 'CALC-307.OUT',
    initialHot: 495,
    initialCold: 540,
  },

  // ── Fired heater ────────────────────────────────────────────────────────
  {
    id: 'SRC-FG',
    name: 'Fuel gas header',
    type: 'source',
    boundaryOnly: true,
    pressure: 4.5e5,
    temperature: 300,
    composition: {},
    profiles: [
      { field: 'pressure', kind: 'constant', mean: 4.5e5, noiseSigma: 0.004, noisePhi: 0.995, lo: 1e5, hi: 8e5 },
    ],
  },
  productValve('FV-202', 60, 0.68),
  {
    id: 'CALC-202',
    name: 'Fuel firing demand',
    type: 'calc',
    // Flow through the fuel valve scales with the square root of header pressure, so a
    // pressure swing on the fuel main is a real disturbance to the coil outlet.
    out: { name: 'H-201.FIRE', units: '%', rangeLo: 0, rangeHi: 130, decimals: 2, description: 'Fuel firing demand' },
    ops: [
      { op: 'tag', tag: 'SRC-FG.P' },
      { op: 'div', value: 4.5 },
      { op: 'sqrt' },
      { op: 'mulTag', tag: 'FV-202.POS' },
      { op: 'clamp', lo: 0, hi: 130 },
    ],
  },
  {
    id: 'H-201',
    name: 'Crude heater',
    type: 'heater',
    lhv: 47.1e6, // natural gas  [PER]
    fuelMax: 1.2, // kg/s
    fuelCommandTag: 'H-201.FIRE',
    thermalEfficiency: 0.88,
    firingTau: 20,
    coilVolume: 14,
    rhoCp: 1.9e6,
    skinRise: 60,
    designDuty: 33.8e6,
    designFlow: 500,
    skinTau: 60,
    initialOutlet: 623,
  },

  // ── Column, condenser and reflux drum ───────────────────────────────────
  {
    id: 'C-301',
    name: 'Atmospheric column',
    type: 'column',
    // A 40 m tower of 5 m diameter: ~900 m³ of vapour space above the liquid. It is what
    // sets how long the operator has when the condenser goes.
    vapourVolume: 900,
    temperature: 500,
    designReflux: 200,
    designFeed: 500,
    bubblePointT: 560,
    designSuperheat: 63,
    vapourMolarAtDesign: 0.556,
    latentMolar: 3.2e7,
    condensateM3PerKmol: 0.13,
    floodVapourLoad: 1.25,
    designPressure: 1.8e5,
    dpDry: 3.5e4,
    condenserUA: 2.15e5,
    coolantFlowAtDesign: 500,
    coolantTTag: 'SRC-CW.T',
    coolantFlowTag: 'S-CW.Q',
    refluxTag: 'S-303.Q',
    feedTag: 'S-101.Q',
    feedQualityTag: 'CALC-QUAL.OUT',
    heaterOutletTag: 'H-201.TOUT',
    bottomsArea: 14,
    bottomsHeight: 4,
    initialBottomsLevel: 2.0,
    cuts: [
      { id: 'NAP', name: 'Naphtha', drawTag: 'S-304.Q', designDraw: 60, tRef: 393, bP: 30, cDraw: 18, dSep: 6, specHi: 438 },
      { id: 'KER', name: 'Kerosene', drawTag: 'CALC-305.OUT', designDraw: 90, tRef: 468, bP: 26, cDraw: 15, dSep: 4 },
      { id: 'DSL', name: 'Diesel', drawTag: 'CALC-306.OUT', designDraw: 125, tRef: 583, bP: 22, cDraw: 12, dSep: 3, specHi: 623 },
      { id: 'RES', name: 'Residue', drawTag: 'CALC-307.OUT', designDraw: 225, tRef: 630, bP: 18, cDraw: 8, dSep: 2 },
    ],
    tauTop: 240,
    tauSide: 420,
    tauBottom: 900,
  },
  {
    id: 'SRC-CW',
    name: 'Condenser cooling water',
    type: 'source',
    pressure: 4e5,
    temperature: 305,
    composition: {},
    profiles: [
      { field: 'temperature', kind: 'diurnal', mean: 305, amplitude: 1.5, periodS: 86400, phaseS: 32400, noiseSigma: 0.0004, noisePhi: 0.999, lo: 288, hi: 330 },
      // Declared so the cooling-loss fault has a header pressure to ramp.
      { field: 'pressure', kind: 'constant', mean: 4e5, noiseSigma: 0.002, noisePhi: 0.995, lo: 1e5, hi: 6e5 },
    ],
  },
  productValve('FV-301', 2000, 0.64, 1.0),
  { id: 'SNK-CW', name: 'Cooling-water return', type: 'sink', pressure: 2.2e5 },
  {
    id: 'D-302',
    name: 'Reflux drum',
    type: 'tank',
    shape: 'horizontal',
    radius: 1.6,
    length: 8,
    height: 3.2,
    initialLevel: 1.6,
    open: true,
    // On a low structure, 3 m above grade. Enough static head to push the naphtha
    // product away, not enough to gravity-feed reflux into a tower at 1.8 bara — that
    // is the reflux pump's job, and losing it really does lose the reflux.
    elevation: 3,
    temperature: 330,
    composition: { light: 0.3, nap: 9.4, ker: 0.3, dsl: 0, res: 0 },
  },
  {
    id: 'P-303',
    name: 'Reflux pump',
    type: 'pump',
    h0: 90,
    kCurve: 3673,
    qBep: 0.07,
    etaBep: 0.76,
    vfd: false,
    commandTag: 'P-303.CMD',
    startTime: 8,
    coastTime: 15,
    npshr: 2.5,
  },
  productValve('FV-303', 380, 0.63),
  productValve('FV-304', 500, 0.61),

  // ── Side draws and bottoms ──────────────────────────────────────────────
  productValve('FV-305', 200, 0.55),
  productValve('FV-306', 260, 0.55),
  productValve('FV-307', 460, 0.55),
  {
    id: 'CALC-305',
    name: 'Kerosene draw',
    type: 'calc',
    out: { name: 'CALC-305.OUT', units: 'm3/h', rangeLo: 0, rangeHi: 200, decimals: 1, description: 'Kerosene draw flow' },
    ops: [{ op: 'tag', tag: 'FV-305.POS' }, { op: 'mul', value: 2.0 }],
  },
  {
    id: 'CALC-306',
    name: 'Diesel draw',
    type: 'calc',
    out: { name: 'CALC-306.OUT', units: 'm3/h', rangeLo: 0, rangeHi: 280, decimals: 1, description: 'Diesel draw flow' },
    ops: [{ op: 'tag', tag: 'FV-306.POS' }, { op: 'mul', value: 2.8 }],
  },
  {
    id: 'CALC-307',
    name: 'Residue draw',
    type: 'calc',
    out: { name: 'CALC-307.OUT', units: 'm3/h', rangeLo: 0, rangeHi: 500, decimals: 1, description: 'Residue draw flow' },
    ops: [{ op: 'tag', tag: 'FV-307.POS' }, { op: 'mul', value: 5.0 }],
  },
  {
    id: 'CALC-QUAL',
    name: 'Crude heaviness',
    type: 'calc',
    // Residue fraction of the assay, 0–1: the single number the reduced-order column
    // uses to represent "how heavy is this crude".
    out: { name: 'CALC-QUAL.OUT', units: '-', rangeLo: 0, rangeHi: 1, decimals: 4, description: 'Crude residue fraction' },
    ops: [
      { op: 'tag', tag: 'SRC-CRUDE.C_res' },
      { op: 'div', value: 10 },
      { op: 'clamp', lo: 0, hi: 1 },
      { op: 'lag', tau: 300 },
    ],
  },

  { id: 'SNK-NAP', name: 'Naphtha to treating', type: 'sink', pressure: 1.05e5 },
  { id: 'SNK-KER', name: 'Kerosene to storage', type: 'sink', pressure: 1.05e5 },
  { id: 'SNK-DSL', name: 'Diesel to storage', type: 'sink', pressure: 1.05e5 },
  { id: 'SNK-RES', name: 'Residue to vacuum unit', type: 'sink', pressure: 1.05e5 },
];

export const streams: StreamConfig[] = [
  { id: 'S-101', from: 'SRC-CRUDE', to: 'HX-101', kind: 'pressure', pump: 'P-101', valve: 'FV-101', k: 4e7, qMax: 0.35, checkValve: true, flowOutTag: 'S-101.Q' },
  { id: 'S-102', from: 'HX-101', to: 'HX-102', kind: 'transfer' },
  { id: 'S-103', from: 'HX-102', to: 'HX-103', kind: 'transfer' },
  { id: 'S-104', from: 'HX-103', to: 'H-201', kind: 'transfer' },
  { id: 'S-201', from: 'H-201', to: 'C-301', kind: 'transfer' },

  // Reflux returns to the column; the overhead condensate leaves it. Both are part of
  // the column's inventory balance, which is why they are real streams.
  { id: 'S-303', from: 'D-302', to: 'C-301', kind: 'pressure', pump: 'P-303', valve: 'FV-303', k: 4e7, qMax: 0.15, checkValve: true, flowOutTag: 'S-303.Q' },
  { id: 'S-302', from: 'C-301', to: 'D-302', kind: 'demand', flowTag: 'C-301.CONDENSATE' },
  { id: 'S-304', from: 'D-302', to: 'SNK-NAP', kind: 'pressure', valve: 'FV-304', k: 1e6, qMax: 0.08, checkValve: true, flowOutTag: 'S-304.Q' },
  { id: 'S-305', from: 'C-301', to: 'SNK-KER', kind: 'demand', flowTag: 'CALC-305.OUT' },
  { id: 'S-306', from: 'C-301', to: 'SNK-DSL', kind: 'demand', flowTag: 'CALC-306.OUT' },
  { id: 'S-307', from: 'C-301', to: 'SNK-RES', kind: 'demand', flowTag: 'CALC-307.OUT' },

  { id: 'S-CW', from: 'SRC-CW', to: 'SNK-CW', kind: 'pressure', valve: 'FV-301', k: 2e6, qMax: 0.35, checkValve: true, flowOutTag: 'S-CW.Q' },
];
